<template>
    <div
        class="aon-dropdown"
        :class="{
            open,
            disabled,
            darkMode,
            'error-alert': error,
            'cluster-dropdown': isClusterDropdown,
            'width-auto': widthAuto,
        }"
        :style="`width: ${width}`"
        v-click-outside="hideDropdown"
    >
        <div v-if="!iconOnlyDropdown" class="label-holder">
            <label v-if="label" class="d-block mb-1" :class="labelCssClass">
                {{ label }}
            </label>
            <span
                class="link"
                v-if="informationIcon"
                v-tooltip="{
                    content: informationIcon,
                    delay: { show: 500, hide: 100 },
                }"
            >
                <font-awesome-icon
                    icon="fal fa-circle-info"
                    :class="`icon mr-3 ${getIconColor}--text`"
                />
            </span>
        </div>

        <AonAlertBox
            v-if="error"
            class="mb-2"
            :isContextual="true"
            :title="errorOptions.errorMsg"
            type="error"
        />
        <div
            v-if="iconOnlyDropdown"
            class="pa-3 dropdown-icon"
            @click="toggleDropdownOpen"
        >
            <font-awesome-icon
                v-if="iconOnlyDropdownOptions"
                :icon="`${iconOnlyDropdownOptions.iconStyle} ${iconOnlyDropdownOptions.iconName}`"
                :class="`icon ${
                    open
                        ? iconOnlyDropdownOptions.iconColorActive
                        : iconOnlyDropdownOptions.iconColor
                }--text`"
            />
        </div>
        <div
            v-else
            :class="[
                'trigger pa-3',
                {
                    border: !triggerBorder,
                    showArrow: showDropDownArrow,
                    showClear: showClear,
                },
            ]"
            @click="toggleDropdownOpen"
        >
            <font-awesome-icon
                v-if="iconOptions"
                :icon="`${iconOptions.iconStyle} ${iconOptions.iconName}`"
                :class="`icon mr-3 ${iconOptions.iconColor}--text`"
            />
            <p
                :class="[
                    {
                        triggerColor:
                            !checkbox &&
                            !selected &&
                            triggerLabel &&
                            placeholderTriggerColor,
                    },
                    `${getWidthClass}`,
                    'mr-2',
                    'overflow-ellipsis',
                ]"
            >
                {{ selectedValue }}
            </p>
            <span
                v-if="!checkbox && showClear && selected.itemText"
                class="icon-container"
                @click.stop="clearSelected"
            >
                <font-awesome-icon
                    icon="far fa-xmark"
                    class="icon castle-moat--text"
                />
            </span>
            <div v-if="showDropDownArrow" class="arrow"></div>
        </div>
        <div
            v-if="items"
            class="relative-trap"
            :style="`top: ${bottomDropdownOffset}`"
        >
            <div
                :class="['items main-items', open, dropdownPos]"
                :style="`width: ${itemWidth};`"
            >
                <div v-if="search" class="search-holder px-4 pt-4 pb-4">
                    <AonInput
                        ref="searchInput"
                        v-model="dropdownSearchTerm"
                        searchInput
                        @update:modelValue="updateSearchTerm"
                    />
                    <div class="search-action-holder mt-4">
                        <p class="bold">{{ selectedValue }}</p>
                        <p class="link ml-4" @click="deselectAll">
                            Deselect All
                        </p>
                        <p class="link ml-4" @click="selectAll">Select All</p>
                    </div>
                </div>
                <ul :style="`text-align: ${textAlign}`">
                    <template
                        v-for="(item, index) in finalItems"
                        :key="`${index}`"
                    >
                        <AonDropdownItem
                            :item="item"
                            :index="index"
                            :checkbox="checkbox"
                            :darkMode="darkMode"
                            :itemColor="itemColor"
                            :itemWidth="itemWidth"
                            :isClusterDropdown="isClusterDropdown"
                            @selected="selectItem"
                        />
                    </template>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup>
import AonCheckbox from './AonCheckbox.vue';
import AonAlertBox from '../AonAlertBox.vue';
import AonInput from './AonInput.vue';
import AonDropdownItem from './AonDropdownItem.vue';

import pluralize from 'pluralize';

import { onMounted, computed, defineExpose, ref, watch } from 'vue';

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return [];
        },
    },
    initSelection: {
        type: Object,
        default: () => {
            return null;
        },
    },
    itemColor: {
        type: String,
        default: 'grey01',
    },
    label: {
        type: String,
        default: '',
    },
    labelCssClass: {
        type: String,
        default: null,
    },
    returnObject: {
        type: Boolean,
        default: false,
    },
    ignorePreset: {
        type: Boolean,
        default: false,
    },
    iconOptions: {
        type: Object,
        default: () => {},
    },
    informationIcon: {
        type: String,
        default: '',
    },
    search: {
        type: Boolean,
        default: false,
    },
    searchItemLabel: {
        type: String,
        default: 'Item',
    },
    // Hoping to have some sort of validator for errors
    error: {
        type: Boolean,
        default: false,
    },
    errorOptions: {
        type: Object,
        default: () => {
            return {
                errorMsg: '',
            };
        },
    },
    width: {
        type: String,
        default: '100%',
    },
    widthAuto: {
        type: String,
        default: null,
    },
    triggerLabel: {
        type: String,
        default: null,
    },
    placeholderTriggerColor: {
        type: Boolean,
        default: false,
    },
    triggerBorder: {
        type: Boolean,
        default: true,
    },
    showDropDownArrow: {
        type: Boolean,
        default: true,
    },
    dropdownPos: {
        type: String,
        default: 'left',
    },
    bottomDropdownOffset: {
        type: String,
        default: null,
    },
    checkbox: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    showClear: {
        type: Boolean,
        default: false,
    },
    isClusterDropdown: {
        type: Boolean,
        default: false,
    },
    darkMode: {
        type: Boolean,
        default: false,
    },
    darkModeBg: {
        type: String,
        default: '',
    },
    iconOnlyDropdown: {
        type: Boolean,
        default: false,
    },
    iconOnlyDropdownOptions: {
        type: Object,
        default: () => {},
    },
    itemWidth: {
        type: String,
        default: '100%',
    },
    textAlign: {
        type: String,
        default: 'left',
    },
});
const emit = defineEmits([
    'select-option',
    'all-selected-options',
    'select-cleared',
]);

const open = ref(false);
const selected = ref({});
const dropdownSearchTerm = ref('');
const searchInput = ref();
const finalItems = ref(props.items);
const selectedItems = ref(props.items);

watch(
    () => props.items,
    () => {
        if (JSON.stringify(props.items) !== JSON.stringify(finalItems.value))
            finalItems.value = props.items;

        props.items.forEach((item, index) => {
            if (item.isDisabled !== undefined) {
                finalItems.value[index].isDisabled = item.isDisabled;
            }
        });
    }
);

const findAndUpdateSubItems = (items, item, val) => {
    items.forEach((i) => {
        if (i.items) {
            findAndUpdateSubItems(i.items, item, val);
        }
        if (i.itemText === item.itemText) {
            i.showSubItems = val;
            return;
        }
        if (i.showSubItems !== undefined) {
            if (i.items.findIndex((x) => x.showSubItems) > -1) return;
            i.showSubItems = false;
        }
    });
};

const findAndUpdateChecked = (items, item, val, parentText) => {
    items.forEach((i) => {
        if (i.itemText === item.itemText) {
            i.checked = val;
            i.parent = parentText;
            return;
        }
        if (i.items) {
            findAndUpdateChecked(i.items, item, val, i.itemText);
        }
    });
};

const selectItem = (item) => {
    const setVal = !item.showSubItems;
    if (item.showSubItems !== undefined) {
        findAndUpdateSubItems(finalItems.value, item, setVal);
    } else {
        if (item.checked !== undefined) {
            const checkVal = !item.checked;
            item.checked = checkVal;
            findAndUpdateChecked(finalItems.value, item, checkVal, null);
        } else {
            open.value = false;
        }
        selected.value = item;

        if (!dropdownSearchTerm.value) {
            selectedItems.value = finalItems.value;
        }
        emit('select-option', selected.value);

        if (props.search || props.checkbox) {
            emit(
                'all-selected-options',
                finalItems.value.filter((item) => item.checked !== false)
            );
        }
    }
};

const setDropDown = (item) => {
    const isSelected = props.returnObject ? item : item;
    open.value = false;
    selected.value = isSelected;
};

const hideDropdown = () => {
    if (!open.value) return;
    open.value = false;
    const subs = finalItems.value.filter((itemInner) => itemInner.showSubItems);
    subs.map((sub) => (sub.showSubItems = false));
};

const deselectAll = () => {
    finalItems.value.forEach((item) => {
        item.checked = false;
    });
    if (!dropdownSearchTerm.value) {
        selectedItems.value = finalItems.value;
    }
};

const selectAll = () => {
    finalItems.value.forEach((item) => {
        item.checked = true;
    });
    if (!dropdownSearchTerm.value) {
        selectedItems.value = finalItems.value;
    }
};

const toggleDropdownOpen = () => {
    open.value = !open.value;
    if (props.search) {
        searchInput.value.setFocus();
    }
};

const updateSearchTerm = (term) => {
    finalItems.value = props.items;
    let filteredItems = finalItems.value.filter((item) =>
        item.itemText.toLowerCase().includes(term.toLowerCase())
    );
    finalItems.value = filteredItems;
};

const pluralizeString = (value, number) => {
    return pluralize(value, Math.floor(number));
};

const selectedValue = computed(() => {
    if (props.search || props.checkbox) {
        return `${totalSelected.value} of ${
            props.items.length
        } ${pluralizeString(
            props.searchItemLabel,
            props.items.length
        )} Selected`;
    }

    if (props.triggerLabel) {
        return props.triggerLabel;
    } else {
        return selected
            ? selected.value.itemText
            : props.triggerLabel
            ? props.triggerLabel
            : finalItems.value[0].itemText;
    }
});

const totalSelected = computed(() => {
    return selectedItems.value.filter((item) => item.checked !== false).length;
});

const getWidthClass = computed(() => {
    if (!props.showDropDownArrow && !props.showClear) {
        return 'full-width';
    } else if (
        (props.showDropDownArrow && !props.showClear) ||
        (!props.showDropDownArrow && props.showClear)
    ) {
        return 'partial-width';
    } else {
        return 'min-width';
    }
});

const getIconColor = computed(() => {
    if (props.iconOptions) {
        return props.iconOptions.iconColor;
    } else if (props.darkMode) {
        return 'grey04';
    } else {
        return 'grey02';
    }
});

onMounted(() => {
    if (finalItems.value.length > 0 && !props.ignorePreset) {
        if (props.initSelection) {
            setDropDown(props.initSelection);
        }
    }
});

const clearSelected = () => {
    open.value = false;
    selected.value = {};
    emit('select-cleared');
};
</script>

<script>
export default {
    name: 'AonDropdown',
};
</script>

<style lang="scss">
.aon-dropdown {
    .search-holder {
        background: white;
        border-bottom: solid 1px $grey01;
    }

    .search-action-holder {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    .checkbox-holder {
        pointer-events: none;
    }

    &.width-auto {
        width: auto;
    }

    &.open {
        .arrow {
            transform: rotate(180deg);
        }
        .main-items {
            pointer-events: all;
            opacity: 1;
            transform: translateY(0);
            max-height: 300px;
            min-width: 200px;
        }
    }

    &.cluster-dropdown {
        .items {
            ul {
                li {
                    &:hover {
                        background: $successBackground;
                    }

                    .description {
                        text-transform: uppercase;
                        font-weight: 600;
                    }

                    .recommended-chip {
                        display: flex;
                        align-items: center;
                        background: $success;
                        border-radius: 30px;

                        p {
                            font-weight: 600;
                        }

                        .icon {
                            width: 20px;
                            height: 17px;
                        }
                    }
                }
            }
        }
    }

    .trigger {
        width: 100%;
        height: 44px;
        position: relative;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        border: solid 1px $grey01;

        display: flex;
        justify-content: flex-start;
        align-items: center;

        transition: border 0.3s ease-in-out;

        &:hover {
            border: solid 1px $castleMoat;
        }

        &.border {
            border: solid 1px transparent;
        }
    }

    .triggerColor {
        color: $grey04;
    }

    .full-width {
        width: 100%;
    }

    .partial-width {
        width: calc(100% - 15px);
    }

    .min-width {
        width: calc(100% - 30px);
    }

    .arrow {
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 7px solid #46535e;
        position: absolute;
        right: 12px;
        border-radius: 2px;

        transition: all 0.3s ease-in-out;
    }

    .relative-trap {
        position: relative;
        z-index: 100000;
    }

    .items {
        width: 100%;
        opacity: 0;
        pointer-events: none;
        border: solid 1px $grey01;

        position: absolute;
        top: 0;
        z-index: 100;

        transform: translateY(30px);
        transition: all 0.3s ease;

        &.left {
            left: 0;
        }

        &.right {
            right: 0;
        }

        ul {
            overflow-y: scroll;
            background: white;
            max-height: 170px;
            box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.2);

            li {
                background: transparent;
                padding: 12px;
                cursor: pointer;
                display: flex;
                justify-content: flex-start;
                align-content: center;
                align-items: center;
                border-top: 2px solid $grey06;
                min-width: 20px;

                transition: all 0.3s ease;

                &:first-of-type {
                    border: none;
                }

                &:hover {
                    background: $grey06;
                }

                &.current {
                    border: 2px solid $success;
                    background: $successBackground;
                }
            }
        }
    }
    .full-width {
        width: 100%;
    }

    .label-holder {
        display: flex;
        justify-content: space-between;
    }

    .icon-container {
        width: 17px;
        height: 17px;
        position: absolute;
        right: 16px;
        top: 13px;
        cursor: pointer;

        .icon {
            width: 100%;
            height: 100%;
        }
    }

    .showClear.showArrow {
        .icon-container {
            top: 12px;
            right: 28px;
        }
    }

    .uppercase {
        text-transform: uppercase;
    }

    &.darkMode {
        &.disabled {
            opacity: 1;
            label,
            .trigger {
                opacity: 0.5;
            }
        }
        label {
            color: $grey01;
        }
        .arrow {
            border-top: 7px solid $grey06;
        }
        .trigger {
            color: $grey06;
            background: $knightsCloak;
            border: solid 1px $grey06;
            &:hover {
                border: solid 1px $knightsCloak;
            }
        }
        .items {
            border: solid 1px $grey06;
        }
        .main-items {
            background: white;
        }
        ul {
            background: $knightsCloak80;
            li {
                border-top: 2px solid $knightsCloak;
                &:hover {
                    background: $knightsCloak;
                }
            }
        }
        .search-holder {
            background: $knightsCloak;
            border-bottom: 1px solid $knightsCloak;
        }
        .search-action-holder .bold {
            color: $grey06;
        }
    }

    .dropdown-icon {
        cursor: pointer;
        border-radius: 50%;

        transition: all 0.3s ease;

        &:hover {
            background: $grey04;
            .icon {
                color: $grey01 !important;
            }
        }
    }

    .sub-items.sub-open {
        pointer-events: all;
        opacity: 1;
        transform: translateY(0);
        max-height: 300px;
    }
}
</style>
